import React, { useState } from 'react';
import {
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  CircularProgress,
  Avatar,
  ListItem,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { useGetQuestionesQuery } from '../../Apis/questionApi';
import { useGetQuizesQuery } from '../../Apis/quizApi';
import { Add} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function QuestionManager() {
  const [selectedQuizId, setSelectedQuizId] = useState<number | null>(null);
  const navigate = useNavigate();

  const { data: quizResponse, isLoading: loadingQuizzes } = useGetQuizesQuery(null);
  const { data: questionResponse, isLoading: loadingQuestions } = useGetQuestionesQuery();

  const quizzes = quizResponse?.result || [];
  const allQuestions = questionResponse?.result || [];

  // Filter logic
  const filteredQuestions = selectedQuizId
    ? allQuestions.filter((q) => q.quizId === selectedQuizId)
    : allQuestions;

  if (loadingQuizzes || loadingQuestions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Grid container spacing={3}>
        
        {/* SIDEBAR - QUIZ SELECTION */}
        <Grid size={{xs:12,md:3}} >
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', height: '80vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, bgcolor: '#512384', color: 'white' }}>
              <Typography variant="h6" fontWeight="bold">Quizzes</Typography>
              <Typography variant="caption">Select a quiz to filter</Typography>
            </Box>
            
            <List sx={{ flexGrow: 1, overflowY: 'auto', py: 0 }} dense>
              <ListItemButton 
                selected={selectedQuizId === null} 
                onClick={() => setSelectedQuizId(null)}
                sx={{ py: 1.5 }}
              >
                <ListItemText primary="View All Questions" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItemButton>
              <Divider />
              {quizzes.map((quiz: any) => (
                <ListItemButton 
                  key={quiz.id} 
                  selected={selectedQuizId === quiz.id}
                  onClick={() => setSelectedQuizId(quiz.id)}
                  sx={{ borderLeft: selectedQuizId === quiz.id ? '4px solid #eb3c96' : '4px solid transparent' }}
                >
                  <ListItemText primary={quiz.name} secondary={`ID: ${quiz.id}`} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* MAIN CONTENT - FILTERED QUESTIONS */}
        <Grid size={{xs:12,md:9}} >
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#512384' }}>
                {selectedQuizId ? `Quiz Content` : "All Questions Library"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                 {filteredQuestions.length} Questions
              </Typography>
            </Box>

            {/* THE NEW NAVIGATE BUTTON */}
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => navigate("/Question/Create")}
              sx={{ 
                bgcolor: '#eb3c96', 
                '&:hover': { bgcolor: '#c22d7a' },
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 'bold'
              }}
            >
              Create Question
            </Button>
          </Box>

          {filteredQuestions.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">No questions available for this selection.</Typography>
            </Paper>
          ) : (
            filteredQuestions.map((q, index) => (
              <Accordion 
                key={index} 
                sx={{ 
                  mb: 1.5, 
                  borderRadius: '12px !important', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '&:before': { display: 'none' } 
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: '#eb3c96' }}
                    >
                      {index + 1}
                    </Avatar>
                    <Typography fontWeight="500">{q.questionName}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#fafafa', borderTop: '1px solid #eee' }}>
                  <List >
                    {q.answers.map((ans, aIndex) => (
                      <ListItem 
                        key={aIndex}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: ans.isCorrect ? 'rgba(76, 175, 80, 0.08)' : 'white',
                          border: ans.isCorrect ? '1px solid #4caf50' : '1px solid #eee'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          {ans.isCorrect ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked fontSize="small" color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={ans.answerName} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            fontWeight: ans.isCorrect ? 'bold' : 'normal',
                            color: ans.isCorrect ? '#2e7d32' : 'text.primary'
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Grid>
      </Grid>
    </Box>
  );
}